//
// # Change Requests Handlers/ Converters
//

// Local Imports
import { logger, Result, Ok, Err, exhaust } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import { layout, model } from "GenAlphaModel";
import * as changes from "DrawCore/src/changes";
import type { LayoutContent, Child } from "./content";

export class ChangeRequests {
  constructor(private content: LayoutContent) {}

  //
  // # Convert a change request
  //
  // Main entry point for most of this class's action.
  // Any array of (anythings) comes in, we validate they are valid changes,
  // look up all the things they refer to, and if successful,
  // return a list of in-memory changes class instances.
  // Essentially everything else in this class is in service of this method.
  //
  readonly convertChangeRequest = async (
    changeList: Array<unknown>
  ): Promise<Result<changes.Combo<Child>>> => {
    logger.log(`converting change request ${changeList}`);
    //
    // Do two steps of conversion: validation, then to our in-memory model
    const { validate } = model.LayoutChange.validator;
    const validations = changeList.map(validate);
    const promises = validations.map((r) => r.andThenAsync(this.convertChange));

    // And this next bit is basically `Result.all` - but we want to keep track of the errors
    const resultsList = await Promise.all(promises);
    const oks = resultsList.filter((r) => r.ok).map((r) => r.val);
    const errs = resultsList.filter((r) => r.err).map((r) => r.val);

    if (errs && errs.length > 0) {
      return Result.Err({
        why: "error-converting-changes",
        errs: JSON.stringify(errs, null, 2),
      });
    }
    if (!oks?.length) {
      return Result.Err({
        why: "no-changes",
        message: "No feasible changes",
      });
    }
    logger.log(`done converting change request ${changeList}`);
    const [first, ...rest] = oks;
    return Result.Ok(changes.Combo.new(first, ...rest));
  };

  // Convert a (serializable) `Change` into an in-memory one
  private convertChange = async (
    change: model.LayoutChange
  ): Promise<Result<changes.Single<Child>>> => {
    //
    switch (change.changeKind) {
      case "add":
        return this.convertAddChange(change);
      case "update":
        return this.convertUpdateChange(change);
      case "remove":
        return this.convertRemoveChange(change);
      case "unsupported":
        return Result.Err({
          why: "unsupported",
          message: "Unsupported Change Request",
        });
      case "set-property":
        // Property changes are handled at the state manager level, not in EditorCore
        return Result.Err({
          why: "not-implemented",
          message:
            "set-property changes are handled at the state manager level",
        });
      case "remove-property":
        // Property changes are handled at the state manager level, not in EditorCore
        return Result.Err({
          why: "not-implemented",
          message:
            "remove-property changes are handled at the state manager level",
        });
      case "set-outline":
        return Result.Err({
          why: "not-implemented",
          message: "set-outline changes are handled at the state manager level",
        });
      case "remove-outline":
        return Result.Err({
          why: "not-implemented",
          message:
            "remove-outline changes are handled at the state manager level",
        });
      default:
        exhaust(change);
    }
  };

  // Convert a `remove` to an in-memory `Change`.
  // Mostly searches for the entity to remove, and returns an error if it can't find it.
  private convertRemoveChange = (
    change: model.LayoutChange & { changeKind: "remove" }
  ): Result<changes.Remove<Child>> => {
    const child = this.content.layoutEntity.find(
      change.data.targetKind,
      change.id
    );
    return child
      ? Ok(changes.remove(child))
      : Result.Err({
          why: "invalid-change-entity-not-found",
          message: "Remove non-existent entity",
        });
  };

  // Warm the layout cache for a segmented placement's per-segment targets.
  // The drawer resolves them with a synchronous `Layout.layouts` lookup and
  // falls back to the instance-level target on a miss -- for an abutted
  // transistor array that is the interior variant, so a miss silently draws a
  // cell with no dummies and no end caps. Param edits mint new variant idents,
  // which are always misses. Mirrors the prefetch importImpl does on open.
  private prefetchSegmentTargets = async (
    place: schema.layout.Place | undefined
  ): Promise<void> => {
    if (place?.kind !== "segments") return;

    const idents = new model.IdentSet();
    for (const seg of place.segments) {
      if (seg?.target) idents.add(seg.target);
    }

    await Promise.all(
      idents.toArray().map(async (ident) => {
        const got = await this.content.getLayout(ident);
        if (got.err) {
          logger.error(
            `could not resolve segment target ${model.Ident.stringify(
              ident
            )}: ${JSON.stringify(got.val)}`
          );
        }
      })
    );
  };

  private convertPartialInstance = async (
    inst: Partial<schema.layout.Instance>
  ): Promise<Result<Partial<layout.Instance>>> => {
    await this.prefetchSegmentTargets(inst.place);

    // For partial updates, target is optional - we only convert it if provided
    if (!inst.target) {
      // No target provided, just return the partial instance as-is
      return Ok(inst as Partial<layout.Instance>);
    }

    // Target was provided, so we need to resolve it
    const layout = await this.content.getLayout(inst.target);
    return layout
      .andThen(
        (layout: layout.Layout): Result<Partial<layout.Instance>> =>
          Ok({ ...inst, target: { kind: "bound", layout } })
      )
      .mapErr((err) =>
        Err({ why: "invalid-change-cannot-find-instance-target", err })
      );
  };

  private convertUpdateInstance = async (change: {
    id: number;
    data: Partial<schema.layout.Instance>;
  }): Promise<Result<changes.Update>> => {
    const existing = this.content.layoutEntity.findInstance(change.id);
    if (!existing) return Err({ why: "invalid-change-instance-not-found" });
    const conv = await this.convertPartialInstance(change.data);
    return conv.andThen((data) =>
      Ok(
        changes.update({
          target: existing,
          from: existing.data,
          to: data,
        })
      )
    );
  };

  private convertInstance = async (
    instance: schema.layout.Instance
  ): Promise<Result<layout.Instance>> => {
    await this.prefetchSegmentTargets(instance.place);
    const getResult = await this.content.getLayout(instance.target);
    return getResult
      .andThen(
        (layout: layout.Layout): Result<layout.Instance> =>
          Ok({ ...instance, target: { kind: "bound", layout } })
      )
      .mapErr((err) =>
        Err({ why: "invalid-change-cannot-find-instance-target", err })
      );
  };

  private convertMetalLayer = (layerId: number): Result<layout.MetalLayer> => {
    const find = (id: number) =>
      this.content.stack?.metalLayers.find((metal) => metal.layer === id);
    const layer = find(layerId);
    return layer
      ? Ok(layer)
      : Result.Err({
          why: "invalid-route",
          message: `Invalid layer ID ${layerId}`,
        });
  };

  private convertMetalLayers = (
    layerIds: Array<number>
  ): Result<Array<layout.MetalLayer>> => {
    return Result.all(...layerIds.map(this.convertMetalLayer));
  };

  private convertWithLayer = <T extends { layer: number }>(
    arg: T
  ): Result<Omit<T, "layer"> & { layer: layout.MetalLayer }> => {
    const { layer } = arg;
    return this.convertMetalLayer(layer).andThen((layer) =>
      Ok({ ...arg, layer })
    );
  };

  private convertPartialWithLayer = <T extends { layer?: number }>(
    arg: T
  ): Result<Omit<T, "layer"> & { layer?: layout.MetalLayer }> => {
    const { layer } = arg;
    if (!layer) return Ok({ ...arg, layer: undefined });
    return this.convertMetalLayer(layer).andThen((layer) =>
      Ok({ ...arg, layer })
    );
  };

  // A pin has no layer of its own to resolve: each shape names its own.
  private convertPin = (
    pin: schema.layout.LayoutPin
  ): Result<layout.LayoutPin> => Ok({ ...pin });
  private convertBlockage = this.convertWithLayer<schema.layout.Blockage>;
  private convertPartialPin = (
    pin: Partial<schema.layout.LayoutPin>
  ): Result<Partial<layout.LayoutPin>> => Ok({ ...pin });
  private convertPartialBlockage = this.convertPartialWithLayer<
    Partial<schema.layout.Blockage>
  >;

  private convertRoute = (route: schema.layout.Route): Result<layout.Route> => {
    const modelPath: layout.ModelRoutePoint[] = [];
    for (const rp of route.path) {
      const layerResult = this.convertMetalLayer(rp.layerId);
      if (!layerResult.ok) return layerResult;
      modelPath.push({
        point: rp.point,
        layer: layerResult.val,
        width: rp.width,
      });
    }
    // Spread so scalar fields (id, locked, width, future) survive; only path
    // is transformed to resolved layers.
    return Ok({ ...route, path: modelPath });
  };

  private convertPartialRoute = (
    route: Partial<schema.layout.Route>
  ): Result<Partial<layout.Route>> => {
    if (!route.path) return Ok({ ...route, path: undefined });
    const modelPath: layout.ModelRoutePoint[] = [];
    for (const rp of route.path) {
      const layerResult = this.convertMetalLayer(rp.layerId);
      if (!layerResult.ok) return layerResult;
      modelPath.push({
        point: rp.point,
        layer: layerResult.val,
        width: rp.width,
      });
    }
    // Spread so scalar fields (id, locked, width, future) survive.
    return Ok({ ...route, path: modelPath });
  };

  private convertUpdateChange = async (
    change: model.LayoutChange & { changeKind: "update" }
  ): Promise<Result<changes.Update>> => {
    const { targetKind } = change.data;
    switch (targetKind) {
      case "pin":
        return this.convertUpdatePin(change);
      case "instance":
        return this.convertUpdateInstance(change);
      case "route":
        return this.convertUpdateRoute(change);
      case "blockage":
        return this.convertUpdateBlockage(change);
      default:
        // exhaust(change);
        return Err({
          why: "not-implemented",
          message: `Failed to update ${targetKind}`,
        });
    }
  };

  private convertUpdatePin = (change: {
    id: number;
    data: Partial<schema.layout.LayoutPin>;
  }): Result<changes.Update> => {
    const existing = this.content.layoutEntity.findPin(change.id);
    if (!existing) return Err({ why: "invalid-change-pin-not-found" });
    return this.convertPartialPin(change.data).andThen((data) =>
      Ok(
        changes.update({
          target: existing,
          from: existing.data,
          to: data,
        })
      )
    );
  };

  private convertUpdateRoute = (change: {
    id: number;
    data: Partial<schema.layout.Route>;
  }): Result<changes.Update> => {
    const existing = this.content.layoutEntity.findRoute(change.id);
    if (!existing) return Err({ why: "invalid-change-route-not-found" });
    return this.convertPartialRoute(change.data).andThen((data) =>
      Ok(
        changes.update({
          target: existing,
          from: existing.data,
          to: data,
        })
      )
    );
  };

  private convertUpdateBlockage = (change: {
    id: number;
    data: Partial<schema.layout.Blockage>;
  }): Result<changes.Update> => {
    const existing = this.content.layoutEntity.findBlockage(change.id);
    if (!existing) return Err({ why: "invalid-change-blockage-not-found" });
    return this.convertPartialBlockage(change.data).andThen((data) =>
      Ok(
        changes.update({
          target: existing,
          from: existing.data,
          to: data,
        })
      )
    );
  };

  private convertAddChange = async (
    change: model.LayoutChange & { changeKind: "add" }
  ): Promise<Result<changes.Add<Child>>> => {
    const { layoutEntity } = this.content;
    const { targetKind } = change.data;
    switch (targetKind) {
      case "pin": {
        return this.convertPin(change.data).andThen((data) => {
          return Ok(changes.add(layoutEntity.createPin(data)));
        });
      }
      case "instance": {
        const convert = await this.convertInstance(change.data);
        return convert.andThen((data) => {
          return Ok(changes.add(layoutEntity.createInstance(data)));
        });
      }
      case "route": {
        return this.convertRoute(change.data).andThen((data) => {
          return Ok(changes.add(layoutEntity.createRoute(data)));
        });
      }
      case "blockage": {
        return this.convertBlockage(change.data).andThen((data) => {
          return Ok(changes.add(layoutEntity.createBlockage(data)));
        });
      }
      default:
        exhaust(targetKind);
    }
  };
}
